import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs';
import { passwordStrengthValidator } from 'src/app/shared/validators/password-strength.validator';
import { environment } from '../../../../environments/environment';
import { SPECIALITIES, TITLES } from '../../../core/data/app.data';
import { UserService } from '../../../core/services/user.service';
import { TurnstileComponent } from '../../../shared/components/turnstile/turnstile.component';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent extends BaseComponent implements OnInit {
  registerForm: FormGroup;
  @Output() loginClick = new EventEmitter<any>();

  @Input() patchEmail?: string;
  @Input() patchType?: string;

  titles = TITLES;
  countries = [{ text: 'South Africa', value: 'South Africa' }];
  specialities = SPECIALITIES;

  passValue: string = '';
  showPassword = false;
  showConfirmPassword = false;

  turnstileToken = '';
  @ViewChild('challenge') challenge?: TurnstileComponent;

  /**
   * True while a registration request is in flight. Guards onSubmit() and disables the
   * submit button — the same flag shape LoginComponent.isLoggingIn uses, deliberately.
   *
   * Without it, every click POSTed /register again. registerUser does a findOne on
   * hashedEmail and then creates, with no constraint underneath, so two requests in that
   * window both find nobody and both insert: three of the nine duplicated hashedEmail
   * values on the live data were created 0.2s, 4.3s and 5.7s apart. The window is seconds
   * wide because the handler runs bcrypt at cost 10 and sends a welcome email.
   *
   * This is a mitigation, not the fix — nothing here binds a second browser, tab or a
   * direct POST. The fix is a unique index on hashedEmail.
   */
  isRegistering = false;

  /** Submit is gated on a solved challenge; skipped when Turnstile isn't configured. */
  get challengeReady(): boolean {
    if (!environment.TURNSTILE_SITE_KEY) return true;
    return !!this.turnstileToken;
  }

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    fs: FeedbackService,
    md: MatDialog
  ) {

    super(fs, md);

    this.registerForm = this.formBuilder.group({
      userType: ['', Validators.required],
      title: ['', Validators.required],
      name: ['', Validators.required],
      surname: ['', Validators.required],
      country: ['South Africa', Validators.required],
      mpNumber: ['', Validators.required],
      speciality: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, passwordStrengthValidator()]],
      confirmPassword: ['', Validators.required]
    }, { validator: this.passwordMatchValidator });

    this.configureFormValidation(this.registerForm.controls);

  }

  ngOnInit(): void {
    this.registerForm.get('userType')!.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.configureFormValidation(this.registerForm.controls);
      });


    if (this.patchEmail) {
      this.registerForm.patchValue({ email: this.patchEmail, userType: this.patchType });
      this.registerForm.get('userType')?.disable();
      this.registerForm.get('email')?.disable();
      this.configureFormValidation(this.registerForm.controls);

    }

  }

  get passwordStrength() {
    const password = this.registerForm.get('password')?.value;
    if (password) {
      return this.userService.calculateStrength(password);
    }
    return 0;
  }

  configureFormValidation(controls: { [key: string]: AbstractControl; }) {
    if (controls['userType'].value === 'doctor') {
      controls['mpNumber'].setValidators([Validators.required]);
      controls['speciality'].setValidators([Validators.required]);
    } else {
      controls['mpNumber'].clearValidators();
      controls['speciality'].clearValidators();
    }

    controls['mpNumber'].updateValueAndValidity();
    controls['speciality'].updateValueAndValidity();

    this.registerForm.updateValueAndValidity();

  }

  onSubmit() {
    // Already registering — ignore the extra submit. Covers a double-click on the button and
    // an impatient second click during the wait, which is the shape that produced the
    // sub-6-second duplicate pairs. Mirrors LoginComponent.onLogin().
    if (this.isRegistering) {
      return;
    }

    if (this.registerForm.valid && this.challengeReady) {
      this.isRegistering = true;
      let { userType, title, name, surname, country, speciality, mpNumber, email, password } = this.registerForm.value;
      userType = userType || this.patchType;
      email = email || this.patchEmail;
      // Set by the login page when they came from a REMOAT sign-up link.
      const remoatSignup = sessionStorage.getItem('remoatSignup') ?? undefined;
      const sub = this.userService.registerUser({ userType, title, name, surname, country, speciality, mpNumber, email, password, turnstileToken: this.turnstileToken, remoatSignup }).pipe(
        // Reset on success, error AND unsubscribe. Doing it in the two handlers instead would
        // leave the button permanently disabled on any path that terminates without calling
        // either — and a register form that cannot be resubmitted is worse than the bug being
        // fixed. The flag is set INSIDE the validity branch, so a bail-out never sets it.
        finalize(() => { this.isRegistering = false; })
      ).subscribe(
        (response) => {
          if (response.success) {
            sessionStorage.removeItem('remoatSignup');
            // Registration successful, emit event to switch back to login form
            this.loginClick.emit({ email, password });
          } else {
            // Registration failed, display error message
            //console.error(response.message);
            if (response.code === 'AUTH_RATE_LIMIT_EXCEEDED') {
              this.showToastMessage('Too many registration attempts. Please try again later');
            }
            else {
              this.showToastMessage('There was an error registering your details. Please check your details and try again');
            }
            //this.showToastMessage(response.message);
          }
          this.resetChallenge();
          sub.unsubscribe();
        },
        (error) => {
          if (error.error.code === 'AUTH_RATE_LIMIT_EXCEEDED') {
            this.showToastMessage('Too many registration attempts. Please try again later');
          }
          else if (error.error?.code === 'TURNSTILE_TOKEN_MISSING' || error.error?.code === 'TURNSTILE_VERIFICATION_FAILED' || error.error?.code === 'TURNSTILE_VERIFICATION_ERROR') {
            this.showToastMessage('Challenge verification failed. Please try again.');
          }
          else {
            this.showToastMessage('There was an error registering your details. Please check your details and try again');
          }
          //console.error('Error registering user:', error);
          this.resetChallenge();
          sub.unsubscribe();
        }
      );
    }
  }

  /** Clears the solved challenge so a failed/duplicate submit must solve a fresh one. */
  private resetChallenge() {
    this.turnstileToken = '';
    this.challenge?.reset();
  }

  onPasswordInput(event: Event) {
    const pass = event.target as HTMLInputElement
    this.passValue = pass.value;
  }

  onLoginClick() {
    this.loginClick.emit();
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ passwordMismatch: true });
    } else {
      formGroup.get('confirmPassword')?.setErrors(null);
    }
  }
}